import { CheckCircleOutlined, EditOutlined, ExclamationCircleOutlined, FileTextOutlined, WarningOutlined } from "@ant-design/icons";
import { Alert, Button, Checkbox, Input, Modal, Space, Spin, Table, Tag, Tooltip } from "antd";
import PropTypes from "prop-types";
import { Component } from "react";

const CONFIDENCE_COLORS = {
    high: "green",
    medium: "orange",
    low: "red",
};

const CONFIDENCE_ICONS = {
    high: <CheckCircleOutlined />,
    medium: <ExclamationCircleOutlined />,
    low: <WarningOutlined />,
};

class FieldDescriptionsModal extends Component {
    constructor(props) {
        super(props);
        this.state = {
            selectedFieldIds: new Set(),
            selectAll: false,
            acceptSchemaDescription: false,
            editingFieldIds: new Set(),
            editedFieldDescriptions: {},
            editingSchemaDescription: false,
            editedSchemaDescription: "",
        };
    }

    componentDidUpdate(prevProps) {
        if (prevProps.visible && !this.props.visible) {
            this.setState({
                selectedFieldIds: new Set(),
                selectAll: false,
                acceptSchemaDescription: false,
                editingFieldIds: new Set(),
                editedFieldDescriptions: {},
                editingSchemaDescription: false,
                editedSchemaDescription: "",
            });
        }
        if (!prevProps.fieldDescriptionsData && this.props.fieldDescriptionsData) {
            const changedFieldIds = new Set();
            const fields = this.props.fieldDescriptionsData?.fields || [];
            const editedFieldDescriptions = fields.reduce((acc, field) => {
                acc[field.fieldId] = field.suggestedDescription || "";
                return acc;
            }, {});
            fields.forEach((field) => {
                if (this.hasChanges(field, editedFieldDescriptions[field.fieldId])) {
                    changedFieldIds.add(field.fieldId);
                }
            });
            const hasSchemaDescChange = this.hasSchemaDescriptionChange();
            this.setState({
                selectedFieldIds: changedFieldIds,
                selectAll: changedFieldIds.size === fields.length && fields.length > 0,
                acceptSchemaDescription: hasSchemaDescChange,
                editingFieldIds: new Set(),
                editedFieldDescriptions,
                editingSchemaDescription: false,
                editedSchemaDescription: this.props.fieldDescriptionsData?.schemaDescription || "",
            });
        }
    }

    getEffectiveSuggestedDescription = (field) => {
        const editedValue = this.state.editedFieldDescriptions[field.fieldId];
        return editedValue !== undefined ? editedValue : field.suggestedDescription;
    };

    getEffectiveSuggestedLabel = (field) => {
        return field.suggestedLabel || field.name;
    };

    hasChanges = (field, suggestedDescriptionOverride) => {
        const suggestedDescription = suggestedDescriptionOverride !== undefined ? suggestedDescriptionOverride : this.getEffectiveSuggestedDescription(field);
        return !!(suggestedDescription && suggestedDescription !== field.description);
    };

    hasSchemaDescriptionChange = () => {
        const data = this.props.fieldDescriptionsData;
        if (!data) return false;
        return data.schemaDescription && data.schemaDescription !== data.currentSchemaDescription;
    };

    isAiAvailable = () => {
        const fields = this.props.fieldDescriptionsData?.fields || [];
        return fields.some((f) => f.confidence !== null);
    };

    onSelectAll = (e) => {
        const checked = e.target.checked;
        const fields = this.props.fieldDescriptionsData?.fields || [];
        if (checked) {
            const allIds = new Set(fields.map((f) => f.fieldId));
            this.setState({ selectedFieldIds: allIds, selectAll: true });
        } else {
            this.setState({ selectedFieldIds: new Set(), selectAll: false });
        }
    };

    onSelectField = (fieldId, checked) => {
        this.setState((prevState) => {
            const newSet = new Set(prevState.selectedFieldIds);
            if (checked) {
                newSet.add(fieldId);
            } else {
                newSet.delete(fieldId);
            }
            const fields = this.props.fieldDescriptionsData?.fields || [];
            return {
                selectedFieldIds: newSet,
                selectAll: newSet.size === fields.length && fields.length > 0,
            };
        });
    };

    getSelectedCount = () => {
        return this.state.selectedFieldIds.size;
    };

    getChangedCount = () => {
        const fields = this.props.fieldDescriptionsData?.fields || [];
        return fields.filter((f) => this.hasChanges(f)).length;
    };

    toggleFieldEditMode = (fieldId) => {
        this.setState((prevState) => {
            const editingFieldIds = new Set(prevState.editingFieldIds);
            if (editingFieldIds.has(fieldId)) {
                editingFieldIds.delete(fieldId);
            } else {
                editingFieldIds.add(fieldId);
            }
            return { editingFieldIds };
        });
    };

    onChangeSuggestedDescription = (fieldId, value) => {
        this.setState((prevState) => ({
            editedFieldDescriptions: {
                ...prevState.editedFieldDescriptions,
                [fieldId]: value,
            },
        }));
    };

    toggleSchemaEditMode = () => {
        this.setState((prevState) => ({
            editingSchemaDescription: !prevState.editingSchemaDescription,
        }));
    };

    handleSave = () => {
        const { fieldDescriptionsData } = this.props;
        const { selectedFieldIds, acceptSchemaDescription } = this.state;

        if (selectedFieldIds.size === 0 && !acceptSchemaDescription) return;

        const fields = fieldDescriptionsData?.fields || [];
        const updatedFields = fields
            .filter((field) => selectedFieldIds.has(field.fieldId))
            .map((field) => {
                const effectiveSuggestion = this.getEffectiveSuggestedDescription(field);
                return {
                    fieldId: field.fieldId,
                    name: field.name,
                    description: effectiveSuggestion && effectiveSuggestion.trim() ? effectiveSuggestion : field.description,
                    active: field.active,
                    isPrimaryKey: field.isPrimaryKey,
                    isSortKey: field.isSortKey,
                    dataType: field.dataType,
                    max: field.max,
                    min: field.min,
                    maxWidth: field.maxWidth,
                    maxHeight: field.maxHeight,
                    maxSize: field.maxSize,
                    isRequired: field.isRequired,
                    isMultivalue: field.isMultivalue,
                    isPII: field.isPII,
                    classifiers: field.classifiers,
                    allowedBooleanValues: field.allowedBooleanValues,
                    validationRules: field.validationRules,
                    picklistType: field.picklistType,
                    dataDomainId: field.dataDomainId,
                    staticPicklist: field.staticPicklist,
                    dateFormat: field.dateFormat,
                    dateSeparator: field.dateSeparator,
                    timeFormat: field.timeFormat,
                    tenantId: field.tenantId,
                    businessAreaId: field.businessAreaId,
                    schemaId: field.schemaId,
                    createdBy: field.createdBy,
                    createdByEmail: field.createdByEmail,
                    updatedBy: field.updatedBy,
                    updatedByEmail: field.updatedByEmail,
                    createdDate: field.createdDate,
                    updatedDate: field.updatedDate,
                    deleted: field.deleted,
                    version: field.version,
                };
            });

        const schemaDesc = acceptSchemaDescription ? this.state.editedSchemaDescription : null;

        if (updatedFields.length > 0 || schemaDesc) {
            this.props.onSave(fieldDescriptionsData.businessAreaId, fieldDescriptionsData.schemaId, updatedFields, schemaDesc);
        }
    };

    getColumns = () => {
        const fields = this.props.fieldDescriptionsData?.fields || [];
        return [
            {
                title: (
                    <Checkbox
                        checked={this.state.selectAll}
                        indeterminate={this.state.selectedFieldIds.size > 0 && this.state.selectedFieldIds.size < fields.length}
                        onChange={this.onSelectAll}
                    />
                ),
                dataIndex: "fieldId",
                key: "select",
                width: 50,
                render: (fieldId) => <Checkbox checked={this.state.selectedFieldIds.has(fieldId)} onChange={(e) => this.onSelectField(fieldId, e.target.checked)} />,
            },
            {
                title: "Field Name",
                dataIndex: "name",
                key: "name",
                width: 130,
                render: (name) => <span className="font-medium">{name}</span>,
            },
            {
                title: "Suggested Label",
                dataIndex: "suggestedLabel",
                key: "suggestedLabel",
                width: 150,
                render: (_, record) => {
                    const suggestedLabel = this.getEffectiveSuggestedLabel(record);
                    const isFallback = suggestedLabel === record.name;
                    return suggestedLabel ? (
                        <Tooltip title={isFallback ? "No AI label suggestion available" : suggestedLabel} placement="topLeft" overlayStyle={{ maxWidth: 400 }}>
                            <span className={isFallback ? "italic text-gray-400" : "text-gray-600"}>{suggestedLabel}</span>
                        </Tooltip>
                    ) : (
                        <span className="italic text-gray-400">—</span>
                    );
                },
            },
            {
                title: "Data Type",
                dataIndex: "dataType",
                key: "dataType",
                width: 90,
                render: (dataType) => <Tag>{dataType}</Tag>,
            },
            {
                title: "Current Description",
                dataIndex: "description",
                key: "description",
                width: 180,
                render: (description) =>
                    description ? (
                        <span className="text-gray-600" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                            {description}
                        </span>
                    ) : (
                        <span className="italic text-gray-400">No description</span>
                    ),
            },
            {
                title: "Suggested Description",
                dataIndex: "suggestedDescription",
                key: "suggestedDescription",
                width: 260,
                render: (_, record) => {
                    const suggestedDescription = this.getEffectiveSuggestedDescription(record);
                    const hasChange = this.hasChanges(record);
                    const isEditing = this.state.editingFieldIds.has(record.fieldId);
                    return (
                        <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                                {isEditing ? (
                                    <Input.TextArea
                                        value={suggestedDescription || ""}
                                        autoSize={{ minRows: 2, maxRows: 6 }}
                                        onChange={(e) => this.onChangeSuggestedDescription(record.fieldId, e.target.value)}
                                    />
                                ) : suggestedDescription ? (
                                    <Tooltip title={suggestedDescription} placement="topLeft" overlayStyle={{ maxWidth: 500 }}>
                                        <span className={hasChange ? "text-blue-600" : "text-gray-600"}>
                                            {suggestedDescription.length > 100 ? suggestedDescription.substring(0, 100) + "..." : suggestedDescription}
                                        </span>
                                    </Tooltip>
                                ) : (
                                    <span className="italic text-gray-400">—</span>
                                )}
                            </div>
                            <Tooltip title={isEditing ? "Done" : "Edit suggested description"}>
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<EditOutlined />}
                                    onClick={() => this.toggleFieldEditMode(record.fieldId)}
                                    className={isEditing ? "text-blue-600" : "text-gray-500"}
                                />
                            </Tooltip>
                        </div>
                    );
                },
            },
            {
                title: "Confidence",
                dataIndex: "confidence",
                key: "confidence",
                width: 100,
                align: "center",
                render: (confidence) =>
                    confidence ? (
                        <Tag icon={CONFIDENCE_ICONS[confidence]} color={CONFIDENCE_COLORS[confidence]}>
                            {confidence.charAt(0).toUpperCase() + confidence.slice(1)}
                        </Tag>
                    ) : (
                        <span className="text-gray-400">—</span>
                    ),
            },
            {
                title: "Reason",
                dataIndex: "reason",
                key: "reason",
                width: 70,
                align: "center",
                render: (reason) =>
                    reason ? (
                        <Tooltip title={reason} placement="topLeft" overlayStyle={{ maxWidth: 400 }}>
                            <ExclamationCircleOutlined className="cursor-pointer text-blue-500" />
                        </Tooltip>
                    ) : (
                        <span className="text-gray-400">—</span>
                    ),
            },
        ];
    };

    render() {
        const { visible, loading, fieldDescriptionsData, saving, onCancel, onRegenerate } = this.props;
        const fields = fieldDescriptionsData?.fields || [];
        const aiAvailable = this.isAiAvailable();
        const changedCount = this.getChangedCount();
        const selectedCount = this.getSelectedCount();
        const hasSchemaDescChange = this.hasSchemaDescriptionChange();

        return (
            <Modal
                title={
                    <div className="flex items-center gap-2">
                        <FileTextOutlined className="text-lg text-blue-600" />
                        <span className="font-semibold">AI Field Descriptions</span>
                        {fieldDescriptionsData?.schemaName && <span className="text-sm font-normal text-gray-500">— {fieldDescriptionsData.schemaName}</span>}
                    </div>
                }
                open={visible}
                onCancel={onCancel}
                width={1200}
                centered
                destroyOnClose
                footer={
                    <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-500">
                            {selectedCount > 0 || this.state.acceptSchemaDescription ? (
                                <span>
                                    {selectedCount} field{selectedCount !== 1 ? "s" : ""} selected
                                    {this.state.acceptSchemaDescription && <span className="ml-2 text-blue-500">+ schema description</span>}
                                </span>
                            ) : (
                                "Select fields to apply description suggestions to form"
                            )}
                        </div>
                        <Space>
                            <Button onClick={onCancel}>Cancel</Button>
                            <Button onClick={onRegenerate} disabled={loading || saving}>
                                Regenerate
                            </Button>
                            <Button type="primary" onClick={this.handleSave} disabled={(selectedCount === 0 && !this.state.acceptSchemaDescription) || saving} loading={saving}>
                                Apply to Form
                            </Button>
                        </Space>
                    </div>
                }
                styles={{
                    body: {
                        maxHeight: "65vh",
                        overflow: "auto",
                        padding: "16px 0",
                    },
                }}
            >
                {loading ? (
                    <div className="flex flex-col items-center justify-center py-16">
                        <Spin size="large" />
                        <p className="mt-4 text-base text-gray-600">Generating field descriptions with AI...</p>
                        <p className="text-sm text-gray-400">This may take up to 30 seconds.</p>
                    </div>
                ) : (
                    <>
                        {!aiAvailable && fields.length > 0 && (
                            <Alert
                                message="AI Descriptions Unavailable"
                                description="AI descriptions are temporarily unavailable. Showing current field values. Please try again later."
                                type="warning"
                                showIcon
                                className="mx-4 mb-4"
                            />
                        )}
                        {changedCount > 0 && aiAvailable && (
                            <Alert
                                message={
                                    <span>
                                        AI generated descriptions for <strong>{changedCount}</strong> field{changedCount !== 1 ? "s" : ""}. Fields with new or improved descriptions
                                        are highlighted and pre-selected.
                                    </span>
                                }
                                type="info"
                                showIcon
                                className="mx-4 mb-4"
                            />
                        )}
                        {changedCount === 0 && aiAvailable && fields.length > 0 && (
                            <Alert
                                message="No description changes suggested. Your current field descriptions appear to be adequate."
                                type="success"
                                showIcon
                                className="mx-4 mb-4"
                            />
                        )}
                        {hasSchemaDescChange && (
                            <div className="mx-4 mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
                                <div className="mb-2 flex items-center justify-between">
                                    <span className="font-medium text-blue-800">Suggested Schema Description</span>
                                    <Space>
                                        <Tooltip title={this.state.editingSchemaDescription ? "Done" : "Edit schema description"}>
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<EditOutlined />}
                                                onClick={this.toggleSchemaEditMode}
                                                className={this.state.editingSchemaDescription ? "text-blue-600" : "text-gray-500"}
                                            />
                                        </Tooltip>
                                        <Checkbox checked={this.state.acceptSchemaDescription} onChange={(e) => this.setState({ acceptSchemaDescription: e.target.checked })}>
                                            Accept
                                        </Checkbox>
                                    </Space>
                                </div>
                                <Input.TextArea
                                    value={this.state.editedSchemaDescription}
                                    autoSize={{ minRows: 2, maxRows: 4 }}
                                    readOnly={!this.state.editingSchemaDescription}
                                    onChange={(e) => this.setState({ editedSchemaDescription: e.target.value })}
                                    className="mb-1"
                                />
                            </div>
                        )}
                        <Table
                            dataSource={fields}
                            columns={this.getColumns()}
                            rowKey="fieldId"
                            pagination={false}
                            size="small"
                            tableLayout="fixed"
                            rowClassName={(record) => (this.hasChanges(record) ? "bg-blue-50" : "")}
                        />
                    </>
                )}
            </Modal>
        );
    }
}

FieldDescriptionsModal.propTypes = {
    visible: PropTypes.bool.isRequired,
    loading: PropTypes.bool,
    saving: PropTypes.bool,
    fieldDescriptionsData: PropTypes.object,
    onCancel: PropTypes.func.isRequired,
    onRegenerate: PropTypes.func,
    onSave: PropTypes.func.isRequired,
};

export default FieldDescriptionsModal;
